import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';
import { Formatters, Messages } from '../../codegen/Localize';
import { AlertActions } from '../../common/react/AlertActions';
import { flowRunErrorMessageFor } from '../../flow/utils/FlowRunErrorMessage';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { AlertErrorCode } from '../../ts/helpers/ErrorCodes';
import { L10n } from '../../ts/L10n/L10n';
import { IAlertInfo } from '../../ts/models/IAlertInfo';
import { DownloadActions } from '../../ts/services/DownloadActions';
import { RunFlowOnOutputStepsAction } from '../../ts/services/RunFlowOnOutputStepsAction';

type AlertActionRow = {
  href?: string;
  onClick: () => void;
  text: string;
};

type AlertDataRow = {
  isUserDefinedValueString?: boolean;
  label: string;
  value: JSX.Element;
};

function createActionRows(
  alert:                      IAlertInfo,
  alertActions:               AlertActions,
  downloadActions:            DownloadActions,
  itemSelected:               () => void,
  resumeRunFlowTasks:         (flowId: string) => void,
  runFlowOnOutputStepsAction: RunFlowOnOutputStepsAction,
  siteUrls:                   SiteUrls,
): ReadonlyArray<AlertActionRow> {
  if (alert.alertType === 'FlowRunFailure') {
    return [
      {// Rerun
        onClick: () => runFlowOnOutputStepsAction.showRunFlowOnOutputStepsDialog(
          alert.flow,
          [alert.flowOutputStepRun.flowOutputStepId],
          alert.flowOutputStepRun.isIncremental),
        text: Messages.errorFlowAlertRerun({ OUTPUT_STEP_NAME: L10n.elideIfLong(alert.flowOutputStepRun.outputName) }),
      },
      {// Download
        onClick: () => downloadActions.downloadFlow(alert.flow),
        text: Messages.errorFlowAlertDownload(),
      }
    ];
  } else if (FeatureFlags.autoSuspendFlowTasksEnabled && alert.alertType === 'FlowTasksSuspended') {
    return [
      {// Task Details
        href: siteUrls.flowScheduledTasksUrl(alert.flow.id),
        onClick: itemSelected,
        text: Messages.scheduledTasksGoToScheduledTasks(),
      },
      {// Resume
        onClick: () => {
          resumeRunFlowTasks(alert.flow.id);
          itemSelected();
        },
        text: Messages.flowTasksResumeSuspendedTasks(),
      },
      {// Download
        onClick: () => downloadActions.downloadFlow(alert.flow),
        text: Messages.errorFlowAlertDownload(),
      }
    ];
  } else {// Workbook out of date
    let actionRows: AlertActionRow[] = [];
    if (alert.datasource || alert.workbooks.length > 0) {
      actionRows.push({// Connection Details
        href: alert.datasource ?
          siteUrls.datasourceDataConnectionUrl(alert.datasource.id) :
          siteUrls.workbookDatasourcesUrl(alert.workbooks[0].id),
        onClick: itemSelected,
        text: Messages.connectionDetails(),
      });
    }
    return actionRows;
  }
}

function createDataRows(
  alert: IAlertInfo,
  alertFailureCountEnabled: boolean,
  alertFailureCountThreshold: number,
  alertLookBackDaysLimit: number,
  flowFailureThresholdForRunPrevention: number,
  itemSelected: () => void,
  siteUrls: SiteUrls,
): ReadonlyArray<AlertDataRow> {
  if (alert.alertType === 'FlowRunFailure') {
    return [
      {// Flow Row
        isUserDefinedValueString: true,
        label: Messages.errorFlowAlertFlow(),
        value: <a href={siteUrls.flowUrl(alert.flow.id)} onClick={itemSelected}>{alert.flow.name}</a>,
      },
      {// Errors
        label: Messages.errorFlowAlertErrors({COUNT: alert.flowOutputStepRun.flowRunErrors.length}),
        value: alertDataRowValueForFlowsErrors(alert)
      },
    ];
  } else if (FeatureFlags.autoSuspendFlowTasksEnabled && alert.alertType === 'FlowTasksSuspended') {
    return [
      {// Flow Row
        isUserDefinedValueString: true,
        label: Messages.errorFlowAlertFlow(),
        value: <a href={siteUrls.flowUrl(alert.flow.id)} onClick={itemSelected}>{alert.flow.name}</a>,
      },
      {// Suspended Type
        label: Messages.tableSuspendedType(),
        value: <>{
          Messages.failureNote({
            COUNT: flowFailureThresholdForRunPrevention,
            COUNT_LOC: Formatters.formatCount(flowFailureThresholdForRunPrevention)
          }) +
          Messages.flowTasksSuspendedCount({
            SUSPENDED_TASK_COUNT: alert.suspendedTasksCount,
            SUSPENDED_TASK_COUNT_LOC: Formatters.formatCount(alert.suspendedTasksCount)
          })
        }</>,
      }
    ];
  } else if(FeatureFlags.extractMoveToTheWebEnabled && alert.alertType === 'CreateExtracts') {// Workbook out of date
    return [
      {// Datasource Row
        isUserDefinedValueString: alert.datasource ? true : false,
        label: Messages.tableDatasource(),
        value: alert.datasource ?
          <a
            href={siteUrls.datasourceDetailsUrl(alert.datasource.id)}
            onClick={itemSelected}>{alert.datasource.name}
          </a> :
          <>{Messages.embedded()}</>
      },
      {// Most Recent Failure Date Row or warning prompt
        label: Messages.extractCreationFailed(),
        value: <>{dateTimeOrNever(alert.mostRecentFailureAt, alertLookBackDaysLimit)}</>,
      },
      {// Resolution Details Row
        label: Messages.tableAlertResolutionDetails(),
        value: <>{getAlertResolutionDetails(alert)}</>,
      },
    ];
  } else {// Workbook out of date
    let notRemoteRefresh = !isRemoteRefresh(alert);
    let notRemoteRefreshingWarning = !isRemoteRefreshWarning(alert);
    return [
      {// Datasource Row
        isUserDefinedValueString: !!alert.datasource,
        label: Messages.tableDatasource(),
        value: alert.datasource ?
          <a
            href={siteUrls.datasourceDetailsUrl(alert.datasource.id)}
            onClick={itemSelected}>{alert.datasource.name}
          </a> :
          <>{Messages.embedded()}</>
      },
      {// Failure Type Row
        label: alert.suspended && alertFailureCountEnabled && notRemoteRefresh ?
          Messages.tableSuspendedType() :
          notRemoteRefreshingWarning ? Messages.tableFailureType() : Messages.tableWarningType(),
        value: <>{failureType(alert, alertFailureCountEnabled && notRemoteRefresh, alertFailureCountThreshold)}</>,
      },
      {// Most Recent Failure Date Row or warning prompt
        label: notRemoteRefreshingWarning ? Messages.tableRefreshFailed() : warningTimestampPrompt(alert),
        value: <>{dateTimeOrNever(alert.mostRecentFailureAt, alertLookBackDaysLimit)}</>,
      },
      {// Most Recent Success Date Row
        label: Messages.tableLastRefresh(),
        value: <>{dateTimeOrNever(alert.lastSuccessAt, alertLookBackDaysLimit)}</>,
      },
      {// Resolution Details Row
        label: Messages.tableAlertResolutionDetails(),
        value: <>{getAlertResolutionDetails(alert)}</>,
      },
    ];
  }
}

function alertDataRowValueForFlowsErrors(alert: IAlertInfo) {
  if (alert.flowOutputStepRun.flowRunErrors == null || alert.flowOutputStepRun.flowRunErrors.length === 0) {
    return <div className={'tb-alert-menu-item-flow-error-row'}>{Messages.errorFlowRunUnknown()}</div>;
  }

  return (
    <>
      {alert.flowOutputStepRun.flowRunErrors.map((error, idx) =>
        <div className={'tb-alert-menu-item-flow-error-row'} key={`alert-menu-item-flow-error-row-${alert.id}-${idx}`}>
          {flowRunErrorMessageFor(error)}
        </div>)}
    </>
  );
}

export type AlertsMenuItemProps = {
  readonly alertActions:                         AlertActions;
  readonly alert:                                IAlertInfo;
  readonly alertFailureCountThreshold:           number;
  readonly alertLookBackDaysLimit:               number;
  readonly alertFailureCountEnabled:             boolean;
  readonly downloadActions:                      DownloadActions;
  readonly flowFailureThresholdForRunPrevention: number;
  readonly itemSelected:                         () => void;
  readonly resumeRunFlowTasks:                   (flowId: string) => void;
  readonly runFlowOnOutputStepsAction:           RunFlowOnOutputStepsAction;
  readonly siteUrls:                             SiteUrls;
};

export class AlertsMenuItem extends React.PureComponent<AlertsMenuItemProps> {
  private memoizedActionRows = defaultMemoize(createActionRows);
  private memoizedDataRows = defaultMemoize(createDataRows);

  public render() {
    if (// This is not an expected case
      FeatureFlags.autoSuspendFlowTasksEnabled &&
      this.props.alert.alertType === 'FlowTasksSuspended' &&
      !this.props.alert.suspendedTasksCount) {
      return null;
    }
    const actionRows = this.memoizedActionRows(
      this.props.alert,
      this.props.alertActions,
      this.props.downloadActions,
      this.props.itemSelected,
      this.props.resumeRunFlowTasks,
      this.props.runFlowOnOutputStepsAction,
      this.props.siteUrls,
    );
    const dataRows = this.memoizedDataRows(
      this.props.alert,
      this.props.alertFailureCountEnabled,
      this.props.alertFailureCountThreshold,
      this.props.alertLookBackDaysLimit,
      this.props.flowFailureThresholdForRunPrevention,
      this.props.itemSelected,
      this.props.siteUrls,
    );
    return (
      <div className={'tb-alert-item'} style={{ whiteSpace: 'normal' }} role={'gridcell'}>
        {renderTitle(this.props.alert)}
        <table cellSpacing={'0'} cellPadding={'0'} role={'presentation'}>
          <tbody>
            {renderAlertDataRows(dataRows)}
            {renderAlertActionRows(actionRows)}
          </tbody>
        </table>
      </div>
    );
  }
}

function failureType(alert: IAlertInfo, alertFailureCountEnabled: boolean, alertFailureCountThreshold: number) {
  if (alertFailureCountEnabled) {
    const count = alert.suspended ? alertFailureCountThreshold : alert.consecutiveFailureCount;
    return Messages.failureNote({ COUNT: count, COUNT_LOC: Formatters.formatCount(count) });
  }
  else {
    return getAlertStatusCode(alert);
  }
}

// Returns the prompt for the timestamp when the warning message was created:
function warningTimestampPrompt(alert: IAlertInfo) {
  if (alert.statusCode === AlertErrorCode.AlertRemoteExtractRefreshNotFinishedOnTime ||
      alert.statusCode === AlertErrorCode.AlertRemoteBridgeClientNotRunning) {
    return Messages.tableAlertCreated();
  } else {
    return Messages.tableRefreshFailed();
  }
}

function dateTimeOrNever(date: Date, alertLookBackDaysLimit: number) {
  if (date) {
    return L10n.localizedDateTime(date);
  } else {// Last Success Not Found
    const days = alertLookBackDaysLimit;

    if (days > 0) {
      return Messages.lastRunSuccess({ LOOK_BACK_DAYS: days, LOOK_BACK_DAYS_LOC: Formatters.formatCount(days) });
    } else {
      return Messages.never();
    }
  }
}

function isRemoteRefresh(alert: IAlertInfo) {
  const remoteRefreshStatusCodes: number[] = [
    AlertErrorCode.AlertRemoteUnknownFailure,
    AlertErrorCode.AlertRemoteSignInFailed,
    AlertErrorCode.AlertRemoteExpiredPassword,
    AlertErrorCode.AlertRemoteNoPassword,
    AlertErrorCode.AlertRemoteConnectionFailure,
    AlertErrorCode.AlertRemoteInvalidFilePath,
    AlertErrorCode.AlertRemoteExtractRefreshNotFinishedOnTime,
    AlertErrorCode.AlertRemoteBridgeClientNotRunning,
  ];
  return _.contains(remoteRefreshStatusCodes, alert.statusCode);
}

// These status codes indicate a warning, not really a failure
function isRemoteRefreshWarning(alert: IAlertInfo) {
  const remoteRefreshWarningStatusCodes: number[] = [
    AlertErrorCode.AlertRemoteExtractRefreshNotFinishedOnTime,
    AlertErrorCode.AlertRemoteBridgeClientNotRunning,
  ];
  return _.contains(remoteRefreshWarningStatusCodes, alert.statusCode);
}

function renderTitle(alert: IAlertInfo) {
  return (
    <h1 className={'tb-alert-menu-item-title'}>
      {alert.alertType === 'FlowRunFailure' ?
        renderTitleFlowRunFailure(alert) :
        FeatureFlags.autoSuspendFlowTasksEnabled && alert.alertType === 'FlowTasksSuspended' ?
          Messages.errorFlowAlertSuspendedTasksTitle({FLOW_NAME: alert.flow.name}) :
          alert.alertType === 'CreateExtracts' ? renderTitleExtractActionFailure(alert):
          renderTitleWorkbookOutOfDate(alert)
      }
    </h1>
  );
}

function renderTitleFlowRunFailure(alert: IAlertInfo) {
  return (
    alert.flowOutputStepRun.flowRunErrors == null || alert.flowOutputStepRun.flowRunErrors.length === 0 ?
        Messages.errorFlowAlertMiscTitle({FLOW_NAME: alert.flow.name}) :
        Messages.errorFlowAlertTitle({
          FLOW_NAME: alert.flow.name,
          ERROR_COUNT: alert.flowOutputStepRun.flowRunErrors.length,
          ERROR_COUNT_LOC: Formatters.formatCount(alert.flowOutputStepRun.flowRunErrors.length,
        )})
  );
}

function renderTitleExtractActionFailure(alert: IAlertInfo) {
  const workbooks = alert.workbooks;
  if (!workbooks || workbooks.length === 0) {
    return Messages.failureExtractCreationDatasource({NAME: L10n.elideIfLong(alert.datasource.name)});
  }

  if (workbooks.length === 1) {
    return Messages.failureExtractCreationWithName({NAME: L10n.elideIfLong(workbooks[0].name)});
  } else {
    // more than one
    return Messages.failureExtractCreationWorkbooksWithCount({
      LIST_COUNT: workbooks.length,
      LIST_COUNT_LOC: Formatters.formatCount(workbooks.length)
    });
  }
}

function renderTitleWorkbookOutOfDate(alert: IAlertInfo) {
  const workbooks = alert.workbooks;
  if (!workbooks || workbooks.length === 0) {
    return Messages.outOfDateWorkbookNone();
  }

  if (workbooks.length === 1) {
    return Messages.outOfDateWithName({NAME: L10n.elideIfLong(workbooks[0].name)});
  } else {
    // more than one
    return Messages.outOfDateWorkbooksWithCount({
      LIST_COUNT: workbooks.length,
      LIST_COUNT_LOC: Formatters.formatCount(workbooks.length)
    });
  }
}

function renderAlertDataRows(dataRows: ReadonlyArray<AlertDataRow>) {
  return (
    <>
      {dataRows.map((row, idx) =>
        <tr key={`alert-data-row-${idx}`}>
          <th className={'tb-alert-menu-item-label'}>{row.label}</th>
          <td className={row.isUserDefinedValueString
              ? 'tb-alert-menu-item-value-user-defined'
              : 'tb-alert-menu-item-value-tableau-defined'}>
            {row.value}
          </td>
        </tr>)}
    </>
  );
}

function renderAlertActionRows(actionRows: ReadonlyArray<AlertActionRow>) {
  return (
    <>
      {actionRows.map((row, idx) =>
        <tr key={`alert-action-row-${idx}`}>
          <td colSpan={2}>
            <a className={'tb-alert-menu-item-action'} href={row.href} onClick={row.onClick}>{row.text}</a>
          </td>
        </tr>)}
    </>
  );
}

function getAlertStatusCode(alert: IAlertInfo) {
  switch (alert.statusCode) {
    case AlertErrorCode.AlertSignInFailed:
    case AlertErrorCode.AlertRemoteSignInFailed:
    case AlertErrorCode.AlertNoPassword:
    case AlertErrorCode.AlertRemoteNoPassword:
    case AlertErrorCode.AlertConnectionlessFailure:
      return Messages.signInFailedUnknownReason();
    case AlertErrorCode.AlertExpiredPassword:
    case AlertErrorCode.AlertRemoteExpiredPassword:
      return Messages.expiredPassword();
    case AlertErrorCode.AlertAccessTokenValidation:
      return Messages.accessTokenValidation();
    case AlertErrorCode.AlertAuthConfiguration:
      return Messages.authConfiguration();
    case AlertErrorCode.AlertConnectionFailure:
    case AlertErrorCode.AlertRemoteConnectionFailure:
      return Messages.connectionFailure();
    case AlertErrorCode.AlertInvalidFilePath:
    case AlertErrorCode.AlertRemoteInvalidFilePath:
      return Messages.invalidFilePath();
    case AlertErrorCode.AlertOverlappingSchedules:
      return Messages.overlappingSchedules();
    case AlertErrorCode.AlertFullRefreshRequired:
      return Messages.fullRefreshRequired();
    case AlertErrorCode.AlertRemoteTableNotFound:
      return Messages.tableNotFound();
    case AlertErrorCode.AlertRemoteUnknownColumn:
      return Messages.unknownColumn();
    case AlertErrorCode.AlertRemoteQueryCompilation:
      return Messages.queryCompilationError();
    case AlertErrorCode.AlertRemoteFederatedError:
      return Messages.federatedError();
    case AlertErrorCode.AlertRemoteInvalidSemiStructDataSchema:
      return Messages.invalidSemiStructDataSchema();
    case AlertErrorCode.AlertRemoteUnableToConnectToFile:
      return Messages.unableToConnectToFile();
    case AlertErrorCode.AlertRemoteNoDriver:
      return Messages.noDriver();
    case AlertErrorCode.AlertRemoteIncompatibleDocumentVersion:
      return Messages.incompatibleDocumentVersion();
    case AlertErrorCode.AlertRemoteExtractRefreshNotFinishedOnTime:
      return Messages.extractRefreshNotFinishedOnTime();
    case AlertErrorCode.AlertRemoteBridgeClientNotRunning:
      return Messages.bridgeClientNotRunning();
    default:
      // AlertErrorCode.AlertUnknownFailure or AlertErrorCode.AlertRemoteUnknownFailure
      return Messages.unknownFailure();
  }
}

function getAlertResolutionDetails(alert: IAlertInfo) {
  switch (alert.statusCode) {
    case AlertErrorCode.AlertSignInFailed:
    case AlertErrorCode.AlertRemoteSignInFailed:
    case AlertErrorCode.AlertNoPassword:
    case AlertErrorCode.AlertRemoteNoPassword:
      return Messages.signInFailedUnknownReasonResolutionDetails();
    case AlertErrorCode.AlertExpiredPassword:
    case AlertErrorCode.AlertRemoteExpiredPassword:
    case AlertErrorCode.AlertAccessTokenValidation:
    case AlertErrorCode.AlertAuthConfiguration:
    case AlertErrorCode.AlertConnectionFailure:
    case AlertErrorCode.AlertRemoteConnectionFailure:
      return Messages.dataConnectionResolutionDetails();
    case AlertErrorCode.AlertInvalidFilePath:
      return Messages.invalidFilePathResolutionDetails();
    case AlertErrorCode.AlertRemoteInvalidFilePath:
      return Messages.invalidFilePathResolutionDetailsRemote();
    case AlertErrorCode.AlertOverlappingSchedules:
      return Messages.overlappingSchedulesResolutionDetails();
    case AlertErrorCode.AlertFullRefreshRequired:
      return Messages.fullRefreshRequiredResolutionDetails();
    case AlertErrorCode.AlertConnectionlessFailure:
      return Messages.connectionlessFailureResolutionDetails();
    case AlertErrorCode.AlertRemoteInvalidSemiStructDataSchema:
      return Messages.invalidSemiStructDataSchemaResolutionDetailsRemote();
    case AlertErrorCode.AlertRemoteUnableToConnectToFile:
      return Messages.unableToConnectToFileResolutionDetailsRemote();
    case AlertErrorCode.AlertRemoteUnknownColumn:
      return Messages.unknownColumnResolutionDetailsRemote();
    case AlertErrorCode.AlertRemoteFederatedError:
      return Messages.federatedErrorResolutionDetailsRemote();
    case AlertErrorCode.AlertRemoteQueryCompilation:
      return Messages.queryCompilationErrorResolutionDetailsRemote();
    case AlertErrorCode.AlertRemoteTableNotFound:
      return Messages.tableNotFoundResolutionDetailsRemote();
    case AlertErrorCode.AlertRemoteNoDriver:
      return Messages.noDriverResolutionDetailsRemote();
    case AlertErrorCode.AlertRemoteIncompatibleDocumentVersion:
      return Messages.incompatibleDocumentVersionResolutionDetailsRemote();
    case AlertErrorCode.AlertRemoteWDCAbortError:
      return Messages.wdcAbortErrorResolutionDetailsRemote();
    case AlertErrorCode.AlertRemoteWDCInvalidData:
      return Messages.wdcInvalidDataResolutionDetailsRemote();
    case AlertErrorCode.AlertRemoteExtractRefreshNotFinishedOnTime:
      return Messages.extractRefreshNotFinishedOnTimeResolutionDetailsRemote();
    case AlertErrorCode.AlertRemoteBridgeClientNotRunning:
      return Messages.bridgeClientNotRunningResolutionDetailsRemote();
    default:
      // AlertErrorCode.AlertUnknownFailure or AlertErrorCode.AlertRemoteUnknownFailure
      return Messages.unknownFailureResolutionDetails();
  }
}
